import React, { useState, useEffect } from 'react';
import { Button, CardText, Carousel, Card } from 'react-bootstrap';
import { ADD_TO_CART } from './utils/actions';
import { idbPromise } from './utils/helpers';

const CARD_W = 250;   // compact card width in px
const CARD_H = 150;   // compact card height, used to keep it on screen

// used when an object has no entity.sizes. Set to [] to hide the selector instead
const DEFAULT_SIZES = ['S', 'M', 'L', 'XL'];

class ObjectTrackCards {
    constructor(objectData, cardWidth, cardHeight) {
        this.name = objectData.entity.description;
        this.price = objectData.entity.price 
            ? parseFloat(objectData.entity.price.toString().replace('$', ''))
            : 0; 
        this.details = objectData.entity.details;
        this.images = Array.isArray(objectData.entity.images)
            ? objectData.entity.images
            : objectData.entity.image
                ? [objectData.entity.image]
                : [];
        this.sizes = Array.isArray(objectData.entity.sizes)
            ? objectData.entity.sizes
            : DEFAULT_SIZES;
        // optional second row (e.g. jeans: sizes = waists, lengths = inseams)
        this.lengths = Array.isArray(objectData.entity.lengths)
            ? objectData.entity.lengths
            : [];
        // colors can be plain strings ("Black") or objects ({ name, hex, images })
        this.colors = Array.isArray(objectData.entity.colors)
            ? objectData.entity.colors.map(c => (typeof c === 'string' ? { name: c } : c))
            : [];
        this.startTime = nullableTimeOffsetToSeconds(objectData.segment.start_time_offset);
        this.endTime = nullableTimeOffsetToSeconds(objectData.segment.end_time_offset);
        this.confidence = objectData.confidence;
        this.cardHeight = cardHeight;
        this.cardWidth = cardWidth;

        this.frames = objectData.frames.map(frame => ({
            card: {
                width: this.cardWidth,
                height: this.cardHeight
            },
            position: {
                x: (frame.normalized_bounding_box?.left || 0) * 100,
                y: (frame.normalized_bounding_box?.top || 0) * 100
            },
            timeOffset: nullableTimeOffsetToSeconds(frame.time_offset)
        }));
    }

    hasFramesForTime(seconds) {
        return this.startTime <= seconds && this.endTime >= seconds;
    }

    currentPosition(seconds) {
        for (let i = 0; i < this.frames.length; i++) {
            if (this.frames[i].timeOffset > seconds) {
                if (i === 0) return null;
                const a = this.frames[i - 1];
                const b = this.frames[i];
                const span = b.timeOffset - a.timeOffset;
                const r = span > 0 ? (seconds - a.timeOffset) / span : 0;
                return {
                    x: a.position.x + (b.position.x - a.position.x) * r,
                    y: a.position.y + (b.position.y - a.position.y) * r
                };
            }
        }
        const last = this.frames[this.frames.length - 1];
        return last ? last.position : null;
    }
}

export { ObjectTrackCards };

export function createTrackCards({ cardTracks, video, setCardContent, dispatch }) {
    if (!Array.isArray(cardTracks)) {
        console.error('Invalid tracks data: Expected an array but received:', typeof cardTracks);
        return;
    }

    const currentTime = video.currentTime;
    const cardMap = new Map();

    cardTracks.forEach(track => {
        if (!(track instanceof ObjectTrackCards)) {
            console.warn('Object is not an instance of ObjectTrackCards:', track);
            return;
        }
        if (!track.hasFramesForTime(currentTime)) return;

        const position = track.currentPosition(currentTime);
        if (!position || cardMap.has(track.name)) return;

        cardMap.set(track.name, {
            position,
            name: track.name,
            price: track.price,
            details: track.details,
            images: track.images,
            sizes: track.sizes,
            lengths: track.lengths,
            colors: track.colors
        });
    });

    const cards = Array.from(cardMap.values());

    setCardContent([
        <TrackCardLayer key={currentTime} cards={cards} dispatch={dispatch} />
    ]);
}

const buttonStyle = {
    backgroundColor: 'rgb(67, 66, 66)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    marginTop: '2%'
};

const buttonTextStyle = {
    margin: 0,
    flex: 1,
    textAlign: 'left',
    color: '#131212',
};

function TrackCardLayer({ cards, dispatch }) {
    const [activeName, setActiveName] = useState(null);
    const activeCard = cards.find(c => c.name === activeName);

    // a card is open: render only its modal, no compact cards at all
    if (activeCard) {
        return (
            <DetailsModal
                dispatch={dispatch}
                name={activeCard.name}
                price={activeCard.price}
                details={activeCard.details}
                images={activeCard.images}
                sizes={activeCard.sizes}
                lengths={activeCard.lengths}
                colors={activeCard.colors}
                onClose={() => setActiveName(null)}
            />
        );
    }

    // nothing open: render all compact cards
    return (
        <>
 {cards.map(({ position, name, images }) => (
    <Card className="card-body-product"
        key={name}
        style={{
            position: 'absolute',
            // keeps a 10px margin from the edges, whatever size the card ends up being
            left: `calc(10px + (100% - 20px) * ${position.x / 100})`,
            top: `calc(10px + (100% - 20px) * ${position.y / 100})`,
            transform: `translate(-${position.x}%, -${position.y}%)`,
            width: 'max-content',
            maxWidth: 'min(clamp(180px, 42vw, 280px), calc(100% - 20px))',
            zIndex: 5,
            pointerEvents: 'auto',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            borderRadius: '8px',
            padding: 'clamp(0.1rem, 1vw, 0.3rem)',
            color: 'white',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            gap: 'clamp(6px, 1.5vw, 12px)',
            textAlign: 'center',
        }}
    >
        {/* left column: image fills the full height */}
        {images?.length > 0 && (
            <div
                style={{
                    position: 'relative',
                    width: 'clamp(34px, 10vw, 60px)',
                    minHeight: 'clamp(60px, 13vw, 90px)',
                    flexShrink: 0,
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    overflow: 'hidden',
                }}
            >
                <img
                    src={images[0]}
                    alt={name}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                    }}
                />
            </div>
        )}

        {/* right column: title on top, button on the bottom */}
      <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 'clamp(2px, 0.6vw, 4px)',
            flex: 1,
            minWidth: 0,
        }}
    >
            <h14 style={{ margin: 0, justifyContent: 'center', textAlign: 'center' }}>
                {name || 'Track Card'}
            </h14>

            <Button
                className="play-details-resume-button "
                style={{
                    padding: '0 clamp(6px, 1.5vw, 8px)',
                    marginTop: 0,
                    height: 'clamp(26px, 5vw, 35px)',
                    minHeight: 0,
                    lineHeight: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                onClick={() => setActiveName(name)}
            >
                <h13 className="play-details-resume-button-text">
                    Details
                </h13>
            </Button>
        </div>
    </Card>
))}
                                </>
                            );
                        }

function DetailsModal({ dispatch, name, price, details, images = [], sizes = [], lengths = [], colors = [], onClose }) {
    const [isAdding, setIsAdding] = useState(false);
    const [selectedSize, setSelectedSize] = useState(null);
    const [selectedLength, setSelectedLength] = useState(null);
    const [selectedColor, setSelectedColor] = useState(null);
    const [visible, setVisible] = useState(false);

    // flip to visible one frame after mounting so the CSS transition runs
    useEffect(() => {
        const id = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(id);
    }, []);

    // play the exit animation, then actually close
    const handleClose = () => {
        onClose();
        };
    const needsSize = sizes.length > 0;
    const needsLength = lengths.length > 0;
    const needsColor = colors.length > 0;

    // work out what's still missing so the button can say so
    const missing = [];
    if (needsColor && selectedColor === null) missing.push('color');
    if (needsSize && selectedSize === null) missing.push(needsLength ? 'waist' : 'size');
    if (needsLength && selectedLength === null) missing.push('length');
    const canAdd = missing.length === 0;
    const missingLabel = `Select ${missing.join(' & ')}`;

    // if the chosen color has its own photos, show those; otherwise the item's default photos
    const colorObj = colors.find(c => c.name === selectedColor);
    const activeImages = colorObj?.images?.length ? colorObj.images : images;

    const addToCart = async () => {
        if (isAdding || !canAdd) return;
        setIsAdding(true);

        // jeans save as "32 x 30"; everything else saves just the size
        const sizeValue = needsLength ? `${selectedSize} x ${selectedLength}` : selectedSize;

        const item = {
            // color is part of the _id so different colors are separate cart items
            _id: selectedColor ? `${name}-${selectedColor}` : (name || 'TrackCard-' + Date.now()),
            name: name || 'Track Card',
            price: price || 0,
            details: details || 'No details',
            size: sizeValue,
            color: selectedColor,
            purchaseQuantity: 1,
        };

        if (dispatch) {
            try {
                dispatch({ type: ADD_TO_CART, item });
                await idbPromise("cart", "put", item);
            } catch (error) {
                console.error('Error with idbPromise:', error);
            } finally {
                setIsAdding(false);
            }
        } else {
            console.error('Dispatch function is not available');
            setIsAdding(false);
        }
    };

    const priceText = price !== null && price !== undefined ? `$${price}` : 'Price not available';

    return (
        <div
            onClick={handleClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 20,
                pointerEvents: 'auto',
                opacity: visible ? 1 : 0,
                transition: visible ? 'opacity 0.25s ease' : 'opacity 0.12s ease',
            }}
        >
            < Card className="card-body-product-expand"
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: 'min(600px, 90vw)',
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'rgba(20, 20, 20, 0.95)',
                    borderRadius: '10px',
                    padding: '1.5rem',
                    color: 'white',
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(16px)',
                    transition: visible
                        ? 'opacity 0.25s ease, transform 0.25s ease'
                        : 'opacity 0.12s ease, transform 0.12s ease',
                }}
            >
                <h5 style={{ flexShrink: 0 }}>{name || 'Track Card'}</h5>
                <h5 style={{ flexShrink: 0 }}>{priceText}</h5>

                {/* everything in here scrolls if it's too tall; buttons stay pinned below */}
                <div style={{ overflowY: 'auto', minHeight: 0, flex: '1 1 auto' }}>
                    {activeImages.length > 0 && (
                        <Carousel
                            key={selectedColor || 'default'}
                            interval={null}
                            controls={activeImages.length > 1}
                            indicators={activeImages.length > 1}
                           style={{ 
                            marginBottom: '1rem',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            borderRadius: '40px',
                            maxWidth: '300px',
                            backgroundColor: 'white',
                            justifyContent: 'center',
                            alignItems: 'center',
                            display: 'flex',
                        }}
                        >
                            {activeImages.map((src, i) => (
                                <Carousel.Item key={i}
                                style={{ backgroundColor: 'white' }}
                                >
                                    <img
                                        src={src}
                                        alt={`${name} ${i + 1}`}
                                        style={{
                                            width: '100%',
                                            height: '260px',
                                            objectFit: 'contain',
                                            backgroundColor: 'white',
                                        }}
                                    />
                                </Carousel.Item>
                            ))}
                        </Carousel>
                    )}

                    {needsColor && (
                        <div style={{ marginBottom: '1rem' }}>
                            <div style={{ marginBottom: '6px' }}>
                                <h5>
                                    Color{selectedColor ? `: ${selectedColor}` : ''}
                                </h5>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {colors.map((c) => {
                                    const isSelected = selectedColor === c.name;
                                    return c.hex ? (
                                        // swatch circle when a hex value is provided
                                        <button
                                            key={c.name}
                                            title={c.name}
                                            aria-label={c.name}
                                            onClick={() => setSelectedColor(c.name)}
                                            style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                backgroundColor: c.hex,
                                                border: isSelected ? '3px solid rgb(200, 0, 0)' : '2px solid #888',
                                                cursor: 'pointer',
                                                padding: 0,
                                            }}
                                        />
                                    ) : (
                                        // text button when there's no hex
                                        <button
                                            key={c.name}
                                            onClick={() => setSelectedColor(c.name)}
                                            style={{
                                                backgroundColor: isSelected ? 'rgb(200, 0, 0)' : 'rgb(67, 66, 66)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '6px 14px',
                                                cursor: 'pointer',
                                                fontWeight: isSelected ? 'bold' : 'normal',
                                            }}
                                        >
                                            {c.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {needsSize && (
                        <div style={{ marginBottom: '1rem' }}>
                            <div style={{ marginBottom: '6px' }}>
                             <h5>
                                {needsLength ? 'Waist' : 'Size'}
                            </h5>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {sizes.map((size) => (
                                    <button
                                        key={size}
                                        onClick={() => setSelectedSize(size)}
                                        style={{
                                            backgroundColor: selectedSize === size ? 'rgb(200, 0, 0)' : 'rgb(67, 66, 66)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '6px 14px',
                                            cursor: 'pointer',
                                            fontSize: 'clamp(16px, 1.8vmax, 18px)',
                                            fontWeight: selectedSize === size ? 'bold' : 'normal',
                                        }}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {needsLength && (
                        <div style={{ marginBottom: '1rem' }}>
                            <div style={{ marginBottom: '6px' }}>
                                <h5>
                                    Length
                                </h5>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {lengths.map((len) => (
                                    <button
                                        key={len}
                                        onClick={() => setSelectedLength(len)}
                                        style={{
                                            backgroundColor: selectedLength === len ? 'rgb(200, 0, 0)' : 'rgb(67, 66, 66)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '6px 14px',
                                            cursor: 'pointer',
                                            fontSize: 'clamp(16px, 1.8vmax, 18px)',
                                            fontWeight: selectedLength === len ? 'bold' : 'normal',
                                        }}
                                    >
                                        {len}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <CardText>
                        {details !== null && details !== undefined ? details : 'Details not available'}
                    </CardText>
                </div>

                <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
                    <Button
                        className="play-details-resume-button "
                        style={buttonStyle}
                        onClick={addToCart}
                        disabled={isAdding || !canAdd}
                    >
                        <h5 className="play-details-resume-button-text" style={buttonTextStyle}>
                            {!canAdd ? missingLabel : isAdding ? 'Adding...' : 'Add to Cart'}
                        </h5>
                    </Button>

                    <Button
                        className="play-details-resume-button "
                        style={buttonStyle}
                        onClick={handleClose}
                    >
                        <h5 className="play-details-resume-button-text" style={buttonTextStyle}>
                            Close
                        </h5>
                    </Button>
                </div>
            </Card>
        </div>
    );
}

export function nullableTimeOffsetToSeconds(timeOffset) {
    if (!timeOffset) return 0;

    let seconds = timeOffset.seconds || 0;
    seconds += timeOffset.nanos / 1e9 || 0;
    return seconds;
}